"use client"

import { useCallback, useRef } from "react"
import { Autocomplete, useJsApiLoader, type Libraries } from "@react-google-maps/api"

// Estable a nivel módulo (mismo id + libraries que GeofenceZoneMapPicker): si se
// recrea en cada render, el loader de Google se re-inicializa y avisa. Comparte
// la misma instancia del script en toda la app.
const LIBRARIES: Libraries = ["places"]

/**
 * Dirección parseada de Google Places. Los nombres espejan `Address` de
 * `lib/delivery/provider.ts` (lo que espera /api/delivery/quote), así que se pasa
 * tal cual al cotizador.
 */
export type SelectedAddress = {
  street: string
  city: string
  state: string
  zip: string
  /** Coordenadas del place. Sin ellas el chequeo de zona queda "unverifiable". */
  lat?: number
  lng?: number
}

type Props = {
  value: string
  /** Texto libre (tecleo directo, o fallback cuando Google no carga). */
  onChange: (v: string) => void
  /** Al elegir una sugerencia: calle + ciudad + estado + ZIP + lat/lng ya parseados. */
  onPlaceSelect: (p: SelectedAddress) => void
  /** Clases del `<input>`. Sin estilo propio: cada superficie pasa el suyo. */
  className?: string
  placeholder?: string
  /** Marca el input como obligatorio en el form que lo contiene. */
  required?: boolean
  autoFocus?: boolean
}

/** Extrae calle/ciudad/estado/ZIP + lat/lng de un `PlaceResult` de Google Places. */
function parsePlace(place: google.maps.places.PlaceResult): SelectedAddress {
  const get = (type: string, useShort = false): string => {
    const c = place.address_components?.find((comp) => comp.types.includes(type))
    return c ? (useShort ? c.short_name : c.long_name) : ""
  }
  const street = [get("street_number"), get("route")].filter(Boolean).join(" ")
  // locality es la ciudad estándar; los fallbacks cubren zonas sin locality.
  const city =
    get("locality") ||
    get("sublocality") ||
    get("postal_town") ||
    get("administrative_area_level_2")
  // short_name → código de 2 letras ("TX").
  const state = get("administrative_area_level_1", true)
  const zip = get("postal_code")
  // Mismo idioma que GeofenceZoneMapPicker: geometry.location son funciones.
  const loc = place.geometry?.location
  const lat = loc ? loc.lat() : undefined
  const lng = loc ? loc.lng() : undefined
  return { street, city, state, zip, lat, lng }
}

/**
 * Input de dirección con Google Places Autocomplete, reusable en cualquier
 * superficie (checkout del cliente, onboarding, admin). Al elegir una sugerencia
 * devuelve calle, ciudad, estado, ZIP **y lat/lng**.
 *
 * ⚠️ `fields` incluye `geometry` a propósito: sin lat/lng el chequeo de zona de
 * delivery devuelve `unverifiable` y falla ABIERTO, o sea el "no llegamos a tu
 * zona" se vuelve decorativo. No quitar `geometry`.
 *
 * Degrada con gracia: sin `NEXT_PUBLIC_GOOGLE_MAPS_API_KEY`, con error de carga,
 * o mientras el script carga, es un `<input>` de texto normal — Google nunca
 * puede romper el checkout, el cliente siempre puede escribir a mano.
 */
export function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  className,
  placeholder,
  required = false,
  autoFocus,
}: Props) {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
  const { isLoaded, loadError } = useJsApiLoader({
    id: "redy-gmaps",
    googleMapsApiKey: apiKey ?? "",
    libraries: LIBRARIES,
  })
  const autoRef = useRef<google.maps.places.Autocomplete | null>(null)

  const onPlaceChanged = useCallback(() => {
    const place = autoRef.current?.getPlace()
    if (!place?.address_components) return
    onPlaceSelect(parsePlace(place))
  }, [onPlaceSelect])

  const input = (
    <input
      type="text"
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={100}
      required={required}
      autoFocus={autoFocus}
      autoComplete="off"
    />
  )

  // Fallback: sin key / error de carga / aún cargando → texto libre.
  if (!apiKey || loadError || !isLoaded) return input

  return (
    <Autocomplete
      onLoad={(a) => { autoRef.current = a }}
      onPlaceChanged={onPlaceChanged}
      options={{
        componentRestrictions: { country: "us" },
        fields: ["address_components", "geometry"],
        types: ["address"],
      }}
    >
      {input}
    </Autocomplete>
  )
}
